import { useState } from "react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import DataTable, { Column } from "@/components/ui/DataTable";
import { StockAdjustment } from "@/redux/services/inventory/inventory.type";
import { formatStock } from "@/lib/utils";
import AdjustmentReasonBadge from "./AdjustmentReasonBadge";
import OrderDetailsDrawer from "@/components/order/OrderDetailsDrawer";
import { usePermissions } from "@/hooks/usePermissions";

interface AdjustmentHistoryTableProps {
  adjustments: StockAdjustment[];
  isLoading: boolean;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  sortDirection: "asc" | "desc";
  onSortDirectionChange: (dir: "asc" | "desc") => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onRowClick: (id: string) => void;
}

const renderReference = (
  adj: StockAdjustment,
  onOrderClick: (id: string) => void,
  canViewPurchaseOrders: boolean
) => {
  if (adj.order) {
    const order = adj.order;
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOrderClick(order.id);
        }}
        className="text-gray-900 hover:text-blue-700 hover:underline"
      >
        {order.orderNumber}
      </button>
    );
  }
  if (adj.purchaseOrder) {
    // Purchase orders need suppliers.view (SFRESTO-165).
    if (!canViewPurchaseOrders) {
      return (
        <span className="text-gray-900">{adj.purchaseOrder.poNumber}</span>
      );
    }
    return (
      <Link
        to={`/inventory/purchase-orders/${adj.purchaseOrder.id}`}
        className="text-gray-900 hover:text-blue-700 hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {adj.purchaseOrder.poNumber}
      </Link>
    );
  }
  if (adj.stockCount) {
    return (
      <Link
        to={`/inventory/stock-counts/${adj.stockCount.id}`}
        className="text-gray-900 hover:text-blue-700 hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        Inventaire
      </Link>
    );
  }
  return <span className="text-gray-400">—</span>;
};

const formatQuantity = (quantity: string, unit: string) => {
  const parsed = parseFloat(quantity);
  const signClass =
    parsed < 0 ? "text-red-600" : parsed > 0 ? "text-green-600" : "";
  const prefix = parsed > 0 ? "+" : "";
  return (
    <span className={`${signClass} font-medium`}>
      {prefix}
      {formatStock(quantity, unit)}
    </span>
  );
};

const AdjustmentHistoryTable = ({
  adjustments,
  isLoading,
  totalCount,
  currentPage,
  pageSize,
  sortDirection,
  onSortDirectionChange,
  onPageChange,
  onPageSizeChange,
  onRowClick,
}: AdjustmentHistoryTableProps) => {
  const [orderDrawerId, setOrderDrawerId] = useState<string | null>(null);
  const { can } = usePermissions();
  const canViewPurchaseOrders = can("suppliers.view");

  const columns: Column[] = [
    {
      key: "createdAt",
      label: "Date",
      nowrap: true,
      sortable: true,
      formatter: (_v, row: StockAdjustment) =>
        format(parseISO(row.createdAt), "d MMM yyyy, HH:mm", { locale: fr }),
    },
    {
      key: "ingredient",
      label: "Ingrédient",
      formatter: (_v, row: StockAdjustment) => (
        <span className="font-medium">{row.ingredient.name}</span>
      ),
    },
    {
      key: "quantity",
      label: "Quantité",
      formatter: (_v, row: StockAdjustment) =>
        row.reason === "cost_correction" ? (
          <span className="text-gray-400">—</span>
        ) : row.packaging && row.packageQuantity !== null ? (
          <div>
            {formatQuantity(row.packageQuantity, row.packaging.name)}
            <span className="block text-[11px] text-muted-foreground">
              = {row.quantity} {row.ingredient.baseUnit}
            </span>
          </div>
        ) : (
          formatQuantity(row.quantity, row.ingredient.baseUnit)
        ),
    },
    {
      key: "reason",
      label: "Raison",
      formatter: (_v, row: StockAdjustment) => (
        <AdjustmentReasonBadge reason={row.reason} />
      ),
    },
    {
      key: "adjustedBy",
      label: "Par",
      formatter: (_v, row: StockAdjustment) =>
        row.adjustedBy?.fullName ?? (
          <span className="text-gray-400 italic">Système</span>
        ),
    },
    {
      key: "reference",
      label: "Référence",
      formatter: (_v, row: StockAdjustment) =>
        renderReference(row, setOrderDrawerId, canViewPurchaseOrders),
    },
  ];

  return (
    <>
    <DataTable
      data={adjustments}
      columns={columns}
      loading={isLoading}
      totalCount={totalCount}
      currentPage={currentPage}
      pageSize={pageSize}
      sortBy="createdAt"
      sortOrder={sortDirection}
      onSort={(_key, order) => onSortDirectionChange(order)}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      onRowClick={(row: StockAdjustment) => onRowClick(row.id)}
      searchable={false}
      emptyMessage="Aucun ajustement pour les filtres sélectionnés"
    />
    <OrderDetailsDrawer
      orderId={orderDrawerId ?? ""}
      isOpen={!!orderDrawerId}
      onClose={() => setOrderDrawerId(null)}
    />
    </>
  );
};

export default AdjustmentHistoryTable;
